import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { CRUNCH_FLAVORS, NUTRITION } from '../mock';
import { MessageCircle, ArrowRight, MapPin, Leaf } from 'lucide-react';

// The 13 individual Crunch flavours are a tasting-menu concept, not
// individually-purchasable WooCommerce products (only the pre-made boxes
// are real products) — so "order" here means "ask us", via WhatsApp,
// rather than linking to a product page that doesn't exist.
const WHATSAPP_NUMBER = '919167798999';
const waLink = (flavorName) =>
  `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(
    `Hi Raretto! I'd love to order the "${flavorName}" Crunch signature. Could you help me with that?`
  )}`;

const FlavorVisual = ({ f, className = '' }) => (
  f.image ? (
    <img src={f.image} alt={f.name} className={className} />
  ) : (
    <div className={`${className} rounded-full flex flex-col items-center justify-center text-[#f6efe1] shadow-2xl`} style={{ background: `linear-gradient(160deg, ${f.color} 0%, #1a0d08 100%)` }}>
      <span className="font-display italic text-xl opacity-70">Raretto</span>
      <span className="tracking-luxe text-[9px] mt-2 opacity-90">CRUNCH</span>
      <span className="font-display text-2xl mt-1">{f.name}</span>
    </div>
  )
);

const Crunch = () => {
  const [activeId, setActiveId] = useState(CRUNCH_FLAVORS[0].id);
  const active = CRUNCH_FLAVORS.find((f) => f.id === activeId);
  const activeNutrition = NUTRITION[activeId];

  return (
    <main>
      {/* HERO */}
      <section className="bg-[#1a120b] text-[#f6efe1] py-24 md:py-32 relative overflow-hidden">
        <div className="absolute -top-40 -right-40 w-96 h-96 bg-[#b78d3c]/12 rounded-full blur-3xl"></div>
        <div className="container-lux relative z-10 max-w-4xl">
          <p className="tracking-luxe text-[#b78d3c] mb-4">The Crunch</p>
          <h1 className="font-display text-6xl md:text-[92px] leading-[1.02] mb-8">
            Thirteen<br />
            <em className="font-serif-alt not-italic text-[#b78d3c]">signatures.</em>
          </h1>
          <p className="text-lg md:text-xl text-[#f6efe1]/75 leading-relaxed max-w-2xl">
            The Crunch is Raretto’s first form, a single piece, tempered by hand, layered with a chosen inclusion. Familiar chocolate. Unfamiliar composition.
          </p>
        </div>
      </section>

      {/* FLAVOUR EXPLORER */}
      <section className="section">
        <div className="container-lux">
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-12 gap-4">
            <div>
              <p className="tracking-luxe text-[#b78d3c] mb-3">The Atelier</p>
              <h2 className="font-display text-5xl md:text-6xl">Choose a flavour.</h2>
            </div>
            <p className="text-[#3a2a1e]/70 max-w-sm">Ten Indian and international expressions, each finished to a house standard.</p>
          </div>

          {/* Flavor pills */}
          <div className="flex flex-wrap gap-2 mb-10">
            {CRUNCH_FLAVORS.map((f) => (
              <button
                key={f.id}
                onClick={() => setActiveId(f.id)}
                className={`px-4 py-2 text-xs tracking-widest uppercase border transition-colors ${activeId === f.id ? 'bg-[#1a120b] text-[#f6efe1] border-[#1a120b]' : 'border-[#1a120b]/25 hover:border-[#1a120b]'}`}
              >{f.name}</button>
            ))}
          </div>

          {/* Active flavour detail */}
          <div className="grid md:grid-cols-2 gap-14 items-center bg-[#efe6d3] p-8 md:p-14">
            <div className="aspect-square rounded-full halo overflow-hidden relative">
              <FlavorVisual f={active} className="w-full h-full object-cover" />
            </div>
            <div className="space-y-6">
              <p className="tracking-luxe text-[#b78d3c]">Signature 0{CRUNCH_FLAVORS.findIndex(f => f.id === activeId) + 1} of 13</p>
              <h3 className="font-display text-5xl leading-tight">{active.name}</h3>
              <p className="font-serif-alt italic text-2xl text-[#1a120b]/85">“{active.couplet}”</p>
              <p className="text-[#3a2a1e]/85 leading-relaxed">{active.tasting}</p>

              <div className="gold-rule max-w-xs"></div>

              <div>
                <p className="tracking-luxe text-[#1a120b]/55 mb-2">Composition</p>
                <p className="text-[#1a120b]">{active.origin}</p>
              </div>

              {/* Nutrition callouts */}
              {activeNutrition && (
                <div className="grid grid-cols-3 gap-3 pt-4">
                  <div className="border-t border-[#1a120b]/15 pt-3">
                    <p className="tracking-luxe text-[#1a120b]/55 text-[10px]">Protein</p>
                    <p className="font-display text-2xl mt-1">{activeNutrition.protein}g</p>
                  </div>
                  <div className="border-t border-[#1a120b]/15 pt-3">
                    <p className="tracking-luxe text-[#1a120b]/55 text-[10px]">Energy</p>
                    <p className="font-display text-2xl mt-1">{activeNutrition.energy}<span className="text-xs">kcal</span></p>
                  </div>
                  <div className="border-t border-[#1a120b]/15 pt-3">
                    <p className="tracking-luxe text-[#1a120b]/55 text-[10px]">Sugar</p>
                    <p className="font-display text-2xl mt-1">{activeNutrition.sugar}g</p>
                  </div>
                </div>
              )}

              <div className="pt-4 flex gap-3">
                <a
                  href={waLink(active.name)}
                  target="_blank"
                  rel="noreferrer"
                  className="btn-whatsapp"
                >
                  Order this signature on WhatsApp <MessageCircle className="w-4 h-4" />
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PHILOSOPHY */}
      <section className="section bg-[#1a120b] text-[#f6efe1]">
        <div className="container-lux grid md:grid-cols-2 gap-16 items-center">
          <div>
            <p className="tracking-luxe text-[#b78d3c] mb-4">Composition</p>
            <h2 className="font-display text-5xl md:text-6xl leading-tight mb-8">Every piece begins with an ingredient.</h2>
            <div className="space-y-5 text-[#f6efe1]/75 leading-relaxed">
              <p>Belgian couverture forms the base of every Crunch. Piedmont hazelnut, Ratnagiri Alphonso, sun-cured rose gulkand, Chikmagalur espresso, chosen where they are grown best.</p>
              <p>The inclusion is folded through at the exact moment the temper allows. Cooled, cut, hand-finished. There is no accelerated production. There is only patience and precision.</p>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            {[
              { t: 'Belgian couverture' },
              { t: 'Piedmont hazelnut' },
              { t: 'Ratnagiri Alphonso' },
              { t: 'Sun-cured rose gulkand' },
              { t: 'Chikmagalur espresso' },
              { t: 'Sicilian orange' },
            ].map((x, i) => (
              <div key={i} className="border border-[#f6efe1]/15 p-5">
                <Leaf className="w-4 h-4 text-[#b78d3c] mb-3" />
                <p className="font-display text-lg">{x.t}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* NUTRITION TABLE */}
      <section className="section">
        <div className="container-lux">
          <div className="mb-10">
            <p className="tracking-luxe text-[#b78d3c] mb-3">The Lab Sheet</p>
            <h2 className="font-display text-5xl md:text-6xl">Nutrition, per 100g.</h2>
            <p className="text-[#3a2a1e]/70 mt-4 max-w-2xl">Independently tested. Nothing hidden.</p>
          </div>

          <div className="overflow-x-auto border border-[#1a120b]/15">
            <table className="w-full text-sm">
              <thead className="bg-[#1a120b] text-[#f6efe1]">
                <tr>
                  <th className="text-left px-4 py-4 font-normal tracking-widest uppercase text-xs">Flavour</th>
                  <th className="text-right px-4 py-4 font-normal tracking-widest uppercase text-xs">Energy (kcal)</th>
                  <th className="text-right px-4 py-4 font-normal tracking-widest uppercase text-xs">Protein (g)</th>
                  <th className="text-right px-4 py-4 font-normal tracking-widest uppercase text-xs">Carbs (g)</th>
                  <th className="text-right px-4 py-4 font-normal tracking-widest uppercase text-xs">Sugar (g)</th>
                  <th className="text-right px-4 py-4 font-normal tracking-widest uppercase text-xs">Sat. Fat (g)</th>
                  <th className="text-right px-4 py-4 font-normal tracking-widest uppercase text-xs">Sodium (mg)</th>
                </tr>
              </thead>
              <tbody>
                {CRUNCH_FLAVORS.map((f, i) => {
                  const n = NUTRITION[f.id];
                  if (!n) return null;
                  return (
                    <tr key={f.id} className={`border-t border-[#1a120b]/12 ${i % 2 === 0 ? 'bg-[#efe6d3]/45' : ''} hover:bg-[#efe6d3] transition-colors`}>
                      <td className="px-4 py-3.5 font-display text-lg">{f.name}</td>
                      <td className="px-4 py-3.5 text-right">{n.energy}</td>
                      <td className="px-4 py-3.5 text-right">{n.protein}</td>
                      <td className="px-4 py-3.5 text-right">{n.carbs}</td>
                      <td className="px-4 py-3.5 text-right">{n.sugar}</td>
                      <td className="px-4 py-3.5 text-right">{n.satFat}</td>
                      <td className="px-4 py-3.5 text-right">{n.sodium}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <p className="text-xs text-[#1a120b]/55 mt-4">Trans fat: ND (&lt;0.05g) across all flavours. Cholesterol negligible.</p>
        </div>
      </section>

      {/* CTA */}
      <section className="section">
        <div className="container-lux max-w-3xl text-center">
          <p className="tracking-luxe text-[#b78d3c] mb-4">Continue</p>
          <h2 className="font-display text-5xl md:text-6xl mb-8">Compose your own selection.</h2>
          <Link to="/build-a-box" className="btn-primary">Curate a Box <ArrowRight className="w-4 h-4" /></Link>
        </div>
      </section>
    </main>
  );
};

export default Crunch;
